/**
 * useTelegramAuth
 *
 * Inside a Telegram WebApp: sends initData to the backend, which verifies
 * the HMAC, creates the user if new, and returns JWT tokens.
 * Auto-login — no dialogs, no merge prompts.
 *
 * Returns { isTelegram, status, error, retry }
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import WebApp from '@twa-dev/sdk'
import { telegramLogin } from '@/api/auth'
import { useAuth } from '@/context/AuthContext'

function detectTelegram() {
  try {
    return (
      typeof window !== 'undefined' &&
      Boolean(window.Telegram?.WebApp) &&
      Boolean(WebApp.initData)
    )
  } catch {
    return false
  }
}

function getStartParam() {
  return (
    WebApp.initDataUnsafe?.start_param ||
    new URLSearchParams(window.location.search).get('startapp') ||
    null
  )
}

export function useTelegramAuth() {
  const { login, isAuth } = useAuth()
  const navigate = useNavigate()

  const [isTelegram, setIsTelegram] = useState(false)
  const [status,     setStatus]     = useState('idle')
  const [error,      setError]      = useState(null)

  const didRun = useRef(false)

  const authenticate = useCallback(async () => {
    // Already logged in — just handle exam code redirect if present
    if (isAuth) {
      setStatus('success')
      const code = getStartParam()
      if (code) navigate(`/join/${code}`)
      return
    }

    const tg = detectTelegram()
    setIsTelegram(tg)
    if (!tg) return

    setStatus('loading')
    setError(null)

    try {
      WebApp.expand()
      WebApp.enableClosingConfirmation()

      const { data } = await telegramLogin(WebApp.initData)

      login({ access: data.access, refresh: data.refresh, user: data.user })
      setStatus('success')

      // Navigate to exam if bot sent a code (works for both new and existing users)
      const code = getStartParam()
      if (code) navigate(`/join/${code}`)
    } catch (err) {
      setError(err.response?.data?.detail ?? err.message ?? 'Telegram auth failed')
      setStatus('error')
    }
  }, [isAuth, login, navigate])

  useEffect(() => {
    if (didRun.current) return
    didRun.current = true
    authenticate()
  }, [authenticate])

  return { isTelegram, status, error, retry: authenticate }
}
